import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import type {
  CreateKeycloakUserResponse,
  Paginated,
  ResetPasswordResponse,
  UserResponse,
} from '../../../api/types';
import { usersKeys } from './keys';

interface UsersListParams extends Record<string, unknown> {
  limit?: number;
  offset?: number;
  search?: string;
  isActive?: boolean;
}

interface UsersListOptions {
  enabled?: boolean;
}

export function useUsersList(params: UsersListParams = {}, options?: UsersListOptions) {
  return useQuery<Paginated<UserResponse>>({
    queryKey: usersKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<UserResponse>>('/admin/users', { params });
      return data;
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useUser(id: string) {
  return useQuery<UserResponse>({
    queryKey: usersKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<UserResponse>(`/admin/users/${id}`);
      return data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

export interface CreateUserInput {
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  isActive?: boolean;
  roleIds?: string[];
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, Error, CreateUserInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post<UserResponse>('/admin/users', input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}

export interface UpdateUserInput {
  id: string;
  body: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    jobTitle?: string;
    department?: string;
    isActive?: boolean;
  };
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, Error, UpdateUserInput>({
    mutationFn: async ({ id, body }) => {
      const { data } = await apiClient.patch<UserResponse>(`/admin/users/${id}`, body);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}

export interface CreateKeycloakUserFields {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
}

export interface CreateKeycloakUserInput {
  id: string;
  body: CreateKeycloakUserFields;
}

export function useCreateKeycloakUser() {
  const queryClient = useQueryClient();
  return useMutation<CreateKeycloakUserResponse, Error, CreateKeycloakUserInput>({
    mutationFn: async ({ id, body }) => {
      const { data } = await apiClient.post<CreateKeycloakUserResponse>(
        `/admin/users/${id}/keycloak`,
        body,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.id) });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/admin/users/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}

export function useResetUserPassword() {
  const queryClient = useQueryClient();
  return useMutation<ResetPasswordResponse, Error, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post<ResetPasswordResponse>(
        `/admin/users/${id}/reset-password`,
      );
      return data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}

export function useAssignRole() {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, Error, { id: string; roleIds: string[] }>({
    mutationFn: async ({ id, roleIds }) => {
      const { data } = await apiClient.post<UserResponse>(`/admin/users/${id}/roles`, { roleIds });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useRemoveRole() {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, Error, { id: string; roleId: string }>({
    mutationFn: async ({ id, roleId }) => {
      const { data } = await apiClient.delete<UserResponse>(`/admin/users/${id}/roles/${roleId}`);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
